import { c as _c } from "react/compiler-runtime";
import { useActivityApiClient } from "@api/activity-api/use-activity-api-client";
import { CreditPurchasesTable } from "@components/table/transactions/credit-purchases-table";
import { TransactionsTable } from "@components/table/transactions/transactions-table";
import { CreditViewMode } from "@custom-types/credit-types";
import { Button, Dialog, FontWeights } from "@flat-ui";
import { CreditsDialogTopRightActions } from "@pages/settings/subscription-settings/credits/credits-dialog-top-right-actions";
import { TransactionsInfoBar } from "@pages/settings/subscription-settings/credits/transactions-info-bar";
import { useCompanyId } from "@context-providers/company-id-provider";
import { CREDIT_DISPLAY_NAME } from "@src/constants/credit-constants";
import { resetCreditTransactionState } from "@store/credit-transactions/credit-transactions-slice";
import { fetchCreditTransactions } from "@store/credit-transactions/credit-transactions-thunk";
import { useAppDispatch } from "@store/store-helper";
import { capitalizeFirstLetter } from "@utils/string-utils";
import { WorkspaceEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useEffect, useState } from "react";
/**
 * A dialog to show credit(token) transactions over time or credit(token) purchases history.
 * It contains a toggle to switch between the two views.
 * @param buttonLabel - The label for the button that opens the dialog.
 * @param initialViewMode - The initial view mode of the dialog.
 */
export function CreditsDialog(t0) {
  const $ = _c(37);
  const {
    buttonLabel,
    initialViewMode
  } = t0;
  const dispatch = useAppDispatch();
  const companyId = useCompanyId();
  let t1;
  if ($[0] !== companyId) {
    t1 = {
      companyId
    };
    $[0] = companyId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== companyId || $[3] !== t1) {
    t2 = {
      companyId,
      activity: t1
    };
    $[2] = companyId;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const activityApiClient = useActivityApiClient(t2);
  const {
    trackEvent
  } = useTrackEvent();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [viewMode, setViewMode] = useState(initialViewMode);
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = capitalizeFirstLetter(CREDIT_DISPLAY_NAME);
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const creditDisplayName = t3;
  let t4;
  let t5;
  if ($[6] !== activityApiClient || $[7] !== dispatch || $[8] !== isDialogOpen || $[9] !== viewMode) {
    t4 = () => {
      if (isDialogOpen && viewMode === CreditViewMode.transaction) {
        dispatch(fetchCreditTransactions({
          activityApiClient
        }));
      }
    };
    t5 = [activityApiClient, dispatch, isDialogOpen, viewMode];
    $[6] = activityApiClient;
    $[7] = dispatch;
    $[8] = isDialogOpen;
    $[9] = viewMode;
    $[10] = t4;
    $[11] = t5;
  } else {
    t4 = $[10];
    t5 = $[11];
  }
  useEffect(t4, t5);
  let t6;
  if ($[12] !== initialViewMode || $[13] !== trackEvent || $[14] !== viewMode) {
    t6 = function handleDialogOpen() {
      trackEvent({
        name: viewMode === CreditViewMode.transaction ? WorkspaceEvents.viewCreditsTransactions : WorkspaceEvents.viewCreditsPurchaseHistory,
        props: {
          origin: "subscription-settings-page"
        }
      });
      setViewMode(initialViewMode);
      setIsDialogOpen(true);
    };
    $[12] = initialViewMode;
    $[13] = trackEvent;
    $[14] = viewMode;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const handleDialogOpen = t6;
  let t7;
  if ($[16] !== dispatch) {
    t7 = function onCreditsDialogClose() {
      setIsDialogOpen(false);
      dispatch(resetCreditTransactionState());
    };
    $[16] = dispatch;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const onCreditsDialogClose = t7;
  let t8;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      padding: "8px 16px",
      fontSize: "12px",
      fontWeight: FontWeights.SemiBold,
      lineHeight: "18px",
      gap: "8px",
      "&:hover": {
        borderRadius: "4px",
        backgroundColor: "action.hover"
      }
    };
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== buttonLabel || $[20] !== handleDialogOpen) {
    t9 = <Button variant="ghost" size="m" onClick={handleDialogOpen} sx={t8}>{buttonLabel}</Button>;
    $[19] = buttonLabel;
    $[20] = handleDialogOpen;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const t10 = viewMode === CreditViewMode.transaction ? `${creditDisplayName} transactions over time` : `${creditDisplayName} purchase history`;
  let t11;
  if ($[22] !== viewMode) {
    t11 = <CreditsDialogTopRightActions currentViewMode={viewMode} setViewMode={setViewMode} />;
    $[22] = viewMode;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  let t13;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      "& .MuiDialog-paper": {
        width: "calc(100% - 4rem)",
        maxWidth: "75rem"
      },
      "& .MuiDialogContent-root": {
        flexGrow: 1
      }
    };
    t13 = <TransactionsInfoBar />;
    $[24] = t12;
    $[25] = t13;
  } else {
    t12 = $[24];
    t13 = $[25];
  }
  let t14;
  if ($[26] !== viewMode) {
    t14 = viewMode === CreditViewMode.transaction ? <TransactionsTable /> : <CreditPurchasesTable />;
    $[26] = viewMode;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== isDialogOpen || $[29] !== onCreditsDialogClose || $[30] !== t10 || $[31] !== t11 || $[32] !== t14) {
    t15 = <Dialog title={t10} open={isDialogOpen} onClose={onCreditsDialogClose} size="l" height="100%" topRightActions={t11} sx={t12}>{t13}{t14}</Dialog>;
    $[28] = isDialogOpen;
    $[29] = onCreditsDialogClose;
    $[30] = t10;
    $[31] = t11;
    $[32] = t14;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== t15 || $[35] !== t9) {
    t16 = <>{t9}{t15}</>;
    $[34] = t15;
    $[35] = t9;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  return t16;
}